import {
  Lock,
  Shield,
  Network,
  Settings,
  Database,
  TriangleAlert,
  Trash2,
  Activity,
  ScanSearch,
  type LucideIcon,
} from 'lucide-react';

import {
  EnvironmentPolicies,
  AppliedPolicy,
} from '@/react/portainer/environments/types';

import { PolicyType, PolicyCategory, PolicyEnvironmentType } from '../types';

/**
 * Icon component for each policy category. Shared for datatable badges and policy type selector.
 */
export function getPolicyIcon(category: PolicyCategory): LucideIcon {
  switch (category) {
    case 'rbac':
      return Lock;
    case 'security':
      return Shield;
    case 'network-security':
      return Network;
    case 'setup':
      return Settings;
    case 'registry':
      return Database;
    case 'change-confirmation':
      return TriangleAlert;
    case 'cleanup':
      return Trash2;
    case 'observability':
      return Activity;
    case 'vulnerability-scanning':
      return ScanSearch;
    default:
      return Shield;
  }
}

/**
 * Metadata for a policy type including display information
 * and supported environment types
 */
export interface PolicyTypeMetadata {
  id: PolicyType;
  category: PolicyCategory;
  displayName: string;
  environmentLabel: string;
  description: string;
  supportedEnvironmentTypes: PolicyEnvironmentType[];
}

const POLICY_TYPES_METADATA: Record<PolicyType, PolicyTypeMetadata> = {
  'rbac-k8s': {
    id: 'rbac-k8s',
    category: 'rbac',
    displayName: 'Kubernetes RBAC',
    environmentLabel: 'Kubernetes',
    description:
      'Control access permissions and role-based access for Kubernetes clusters',
    supportedEnvironmentTypes: ['kubernetes'],
  },
  'security-k8s': {
    id: 'security-k8s',
    category: 'security',
    displayName: 'Kubernetes Security',
    environmentLabel: 'Kubernetes',
    description:
      'Enforce security policies and best practices for Kubernetes clusters',
    supportedEnvironmentTypes: ['kubernetes'],
  },
  'network-security-k8s': {
    id: 'network-security-k8s',
    category: 'network-security',
    displayName: 'Kubernetes Network Security',
    environmentLabel: 'Kubernetes',
    description:
      'Enforce network security policies to control the flow of traffic within your cluster',
    supportedEnvironmentTypes: ['kubernetes'],
  },
  'setup-k8s': {
    id: 'setup-k8s',
    category: 'setup',
    displayName: 'Kubernetes Setup',
    environmentLabel: 'Kubernetes',
    description:
      'Configure cluster settings, resources, and deployment options',
    supportedEnvironmentTypes: ['kubernetes'],
  },
  'registry-k8s': {
    id: 'registry-k8s',
    category: 'registry',
    displayName: 'Kubernetes Registry',
    environmentLabel: 'Kubernetes',
    description: 'Manage registry access and configuration for Kubernetes',
    supportedEnvironmentTypes: ['kubernetes'],
  },
  'rbac-docker': {
    id: 'rbac-docker',
    category: 'rbac',
    displayName: 'RBAC',
    environmentLabel: 'Docker',
    description:
      'Control access permissions and role-based access for Docker environments',
    supportedEnvironmentTypes: ['docker', 'podman', 'swarm'],
  },
  'security-docker': {
    id: 'security-docker',
    category: 'security',
    displayName: 'Security',
    environmentLabel: 'Docker',
    description:
      'Enforce security policies and best practices for Docker environments',
    supportedEnvironmentTypes: ['docker', 'podman', 'swarm'],
  },
  'setup-docker': {
    id: 'setup-docker',
    category: 'setup',
    displayName: 'Setup',
    environmentLabel: 'Docker',
    description: 'Configure Docker environment settings and deployment options',
    supportedEnvironmentTypes: ['docker', 'podman', 'swarm'],
  },
  'registry-docker': {
    id: 'registry-docker',
    category: 'registry',
    displayName: 'Registry',
    environmentLabel: 'Docker',
    description:
      'Manage container registry access and configuration for Docker',
    supportedEnvironmentTypes: ['docker', 'podman', 'swarm'],
  },
  'change-confirmation': {
    id: 'change-confirmation',
    category: 'change-confirmation',
    displayName: 'Banner and change confirmation',
    environmentLabel: 'All',
    description:
      'Configure visual environment highlights and confirmation for changes',
    supportedEnvironmentTypes: ['kubernetes', 'docker', 'podman', 'swarm'],
  },
  'cleanup-docker': {
    id: 'cleanup-docker',
    category: 'cleanup',
    displayName: 'Image cleanup',
    environmentLabel: 'Docker / Podman / Swarm',
    description:
      'Automatically garbage-collect old or unused images to reclaim disk space.',
    supportedEnvironmentTypes: ['docker', 'podman', 'swarm'],
  },
  'observability-k8s': {
    id: 'observability-k8s',
    category: 'observability',
    displayName: 'Observability (OneUptime)',
    environmentLabel: 'Kubernetes',
    description: 'Connect Kubernetes clusters to OneUptime for observability',
    supportedEnvironmentTypes: ['kubernetes'],
  },
  'pod-security-standards-k8s': {
    id: 'pod-security-standards-k8s',
    category: 'security',
    displayName: 'Pod Security Standards',
    environmentLabel: 'Kubernetes',
    description:
      'Configure Kubernetes Pod Security Standards profiles per namespace',
    supportedEnvironmentTypes: ['kubernetes'],
  },
  'vulnerability-scanning-k8s': {
    id: 'vulnerability-scanning-k8s',
    category: 'vulnerability-scanning',
    displayName: 'Vulnerability Scanning',
    environmentLabel: 'Kubernetes',
    description:
      'Scan the images running in your clusters for known vulnerabilities',
    supportedEnvironmentTypes: ['kubernetes'],
  },
};

/**
 * Get metadata for a specific policy type
 */
export function getPolicyTypeMetadata(
  policyType: PolicyType
): PolicyTypeMetadata {
  return POLICY_TYPES_METADATA[policyType];
}

/**
 * Policy types with a backend contract but no creation form yet. Excluded
 * from getAllPolicyTypesMetadata so they can't be selected in the create
 * wizard; remove once the type has a working PolicyFormByType case.
 */
const UNRELEASED_POLICY_TYPES: ReadonlySet<PolicyType> = new Set([]);

/**
 * Get all policy type metadata as an array, excluding types not yet ready
 * for the create wizard (see UNRELEASED_POLICY_TYPES).
 */
export function getAllPolicyTypesMetadata(): PolicyTypeMetadata[] {
  return Object.values(POLICY_TYPES_METADATA).filter(
    (metadata) => !UNRELEASED_POLICY_TYPES.has(metadata.id)
  );
}

/**
 * Mapping between EnvironmentPolicies field names and PolicyType
 * Used to iterate through policies and extract applied policy information
 */
const POLICY_FIELD_MAPPING: Array<{
  key: keyof EnvironmentPolicies;
  type: PolicyType;
  label: string;
}> = [
  { key: 'k8sSecurityPolicy', type: 'security-k8s', label: 'Security policy' },
  {
    key: 'k8sNetworkSecurityPolicy',
    type: 'network-security-k8s',
    label: 'Network Security policy',
  },
  { key: 'k8sSetupPolicy', type: 'setup-k8s', label: 'Setup policy' },
  { key: 'k8sRBACPolicy', type: 'rbac-k8s', label: 'RBAC policy' },
  {
    key: 'k8sRegistryPolicy',
    type: 'registry-k8s',
    label: 'Registry policy',
  },
  {
    key: 'k8sObservabilityPolicy',
    type: 'observability-k8s',
    label: 'Observability policy',
  },
  {
    key: 'k8sPodSecurityStandardsPolicy',
    type: 'pod-security-standards-k8s',
    label: 'Pod Security Standards policy',
  },
  {
    key: 'k8sVulnerabilityScanningPolicy',
    type: 'vulnerability-scanning-k8s',
    label: 'Vulnerability Scanning policy',
  },
  {
    key: 'dockerSecurityPolicy',
    type: 'security-docker',
    label: 'Security policy',
  },
  { key: 'dockerSetupPolicy', type: 'setup-docker', label: 'Setup policy' },
  { key: 'dockerRBACPolicy', type: 'rbac-docker', label: 'RBAC policy' },
  {
    key: 'dockerRegistryPolicy',
    type: 'registry-docker',
    label: 'Registry policy',
  },
  {
    key: 'changeConfirmationPolicy',
    type: 'change-confirmation',
    label: 'Banner and change confirmation policy',
  },
  {
    key: 'dockerCleanupPolicy',
    type: 'cleanup-docker',
    label: 'Image cleanup policy',
  },
];

/**
 * Information about an applied policy from EnvironmentPolicies
 */
export interface AppliedPolicyInfo {
  key: keyof EnvironmentPolicies;
  label: string;
  type: PolicyType;
  policy: AppliedPolicy;
}

/**
 * Intermediate type used during mapping before filtering
 * Policy may be undefined until we filter for applied policies
 */
interface MappedPolicyItem {
  key: keyof EnvironmentPolicies;
  label: string;
  type: PolicyType;
  policy: AppliedPolicy | undefined;
}

/**
 * Type guard to check if a mapped policy item has an applied policy with a policyID
 */
function isAppliedPolicyInfo(
  item: MappedPolicyItem
): item is AppliedPolicyInfo {
  return item.policy !== undefined && item.policy.policyID !== undefined;
}

/**
 * Get applied policies for a Kubernetes environment
 * Filters to only return policies that have been applied (have a policyID)
 */
export function getK8sAppliedPolicies(
  policies: EnvironmentPolicies | undefined
): AppliedPolicyInfo[] {
  if (!policies) {
    return [];
  }

  return POLICY_FIELD_MAPPING.filter(
    (mapping) =>
      mapping.type.endsWith('-k8s') || mapping.type === 'change-confirmation'
  )
    .map((mapping) => ({
      key: mapping.key,
      label: mapping.label,
      type: mapping.type,
      policy: policies[mapping.key],
    }))
    .filter(isAppliedPolicyInfo);
}

/**
 * Get applied policies for a Docker environment
 * Filters to only return policies that have been applied (have a policyID)
 */
export function getDockerAppliedPolicies(
  policies: EnvironmentPolicies | undefined
): AppliedPolicyInfo[] {
  if (!policies) {
    return [];
  }

  return POLICY_FIELD_MAPPING.filter(
    (mapping) =>
      mapping.type.endsWith('-docker') || mapping.type === 'change-confirmation'
  )
    .map((mapping) => ({
      key: mapping.key,
      label: mapping.label,
      type: mapping.type,
      policy: policies[mapping.key],
    }))
    .filter(isAppliedPolicyInfo);
}
